import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';

import { ClosableModalWithErrorResponseModel } from '../../base/closable-modal-with-error-response-model';
import {
  Container,
  Message,
  CreateBackgroundInfoTemplatePayload,
  ImportTemplateErrorResponse
} from '../../../models';
import { TranslationConstants } from '../../../constants/translation';

import { ModalService, ConfirmationModalComponent } from '../../../modules/modal';
import { NotificationService, BackgroundInfoService, SessionService, UtilsService } from '../../../services';
import { PaginationConstants } from '../../../constants/pagination';
import { BackgroundInfoTemplate } from '../../../models/background-info-template';

@Component({
  selector: 'import-background-info-template',
  templateUrl: './import-background-info-template.component.html',
})
export class ImportBackgroundInfoTemplateComponent
  extends ClosableModalWithErrorResponseModel<ImportTemplateErrorResponse>
  implements OnInit, OnDestroy {

  data: CreateBackgroundInfoTemplatePayload;
  workingContainer: Container;
  jurisdictionId: number;
  currentPage = 1;
  totalItems: number;
  pageSizes = PaginationConstants.PAGE_SIZES;
  selectedPageSize = this.pageSizes[0];
  selectedTemplateId: number;
  backgroundInfoTemplates: BackgroundInfoTemplate[] = [];

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _backgroundInfoService: BackgroundInfoService,
    private _sessionService: SessionService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _utilsService: UtilsService
  ) {
    super(notificationService, modalService);
    this.workingContainer = this._sessionService.getWorkingContainer();
  }

  ngOnInit(): void {
    this.jurisdictionId = this._backgroundInfoService.getJurisdictionId(this.data.activeJurisdiction);
    this.listenForErrorMessages();
    this.getBackgroundInfoTemplates();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  getUseCaseKey(isPlural: boolean = false): string {
    return this._backgroundInfoService.getTranslationKeyByBackInfoType(this.data.useCase, isPlural);
  }

  getImportTemplateModalTitle(): string {
    const useCaseKey = this._backgroundInfoService.getTranslationKeyByBackInfoType(this.data.useCase).toLowerCase();
    return `background_info.import_${useCaseKey.replace('global.', '')}_background_info_template`;
  }

  getObjectCodeTranslationKey(): string {
    return this._backgroundInfoService.getObjectCodeTranslationKey(this.data.useCase);
  }

  getJurisdictionIsoCode(): string {
    return this._backgroundInfoService.getJurisdictionIsoCode(this.data.activeJurisdiction);
  }

  isTransactionType(): boolean {
    return this._backgroundInfoService.isTransactionType(this.data.useCase);
  }

  pageChanged(event: any): void {
    this.currentPage = event.page;
    this.getBackgroundInfoTemplates();
  }

  isTemplateSelected({ backgroundInfoTemplateRefId }: BackgroundInfoTemplate): boolean {
    return backgroundInfoTemplateRefId === this.selectedTemplateId;
  }

  select({ backgroundInfoTemplateRefId }: BackgroundInfoTemplate): void {
    if (this.selectedTemplateId === backgroundInfoTemplateRefId) {
      this.selectedTemplateId = null;
    } else {
      this.selectedTemplateId = backgroundInfoTemplateRefId;
    }
  }

  toggleCollapse(template: BackgroundInfoTemplate): void {
    template.collapsed = !template.collapsed;
  }

  importTemplate(): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    this.resetErrors();
    this.modalService.openModal({
      component: ConfirmationModalComponent,
      data: {
        title: TranslationConstants.BACKGROUND_INFO_TEMPLATE_IMPORT_MODAL_TITLE,
        content: TranslationConstants.BACKGROUND_INFO_TEMPLATE_IMPORT_MODAL_WARNING,
      } as Message,
      success: () => {
        this._backgroundInfoService
          .importTemplate(this.selectedTemplateId, this.data.useCase, this.data.objectId)
          .subscribe(() => {
            this.closeAllModals();
            this._backgroundInfoService.reFetchBackgroundInfo(this.data.activeJurisdiction);
          });
      },
      cancel: () => {
        this.isSaveDisabled = false;
      },
    });
  }

  collapseAll(): void {
    this._utilsService.collapseAll(this.backgroundInfoTemplates);
    this._changeDetectorRef.detectChanges();
  }

  getBackgroundInfoTemplates(): void {
    this._backgroundInfoService
      .getBackgroundInfoTemplates(this.data.useCase, this.jurisdictionId, this.currentPage, this.selectedPageSize)
      .subscribe(({ data, count }) => {
        this.backgroundInfoTemplates = data;
        this.totalItems = count;
        this.collapseAll();
      });
  }
}
